import React, { useState } from 'react';
import { Table, Collapse, Tooltip, Progress, Button } from 'antd';
import { DownloadOutlined, BarChartOutlined } from '@ant-design/icons';
import PoaMediosVerificacion from './PoaMerdiosVerificacion';
import './Poa.css';

const PoaActividades = ({ nodo }) => {

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const showModal = (record) => {
        setSelectedRecord(record);
        setIsModalVisible(true);
    };

    const hideModal = () => {
        setIsModalVisible(false);
        setSelectedRecord(null);
    };

    // Función para extraer datos de actividades e indicadores
    const extractData = (children) => {
        return children.flatMap(child => {
            if (child.children && child.tipo === 'Actividad') {

                const actividadData = child.children.flatMap(grandchild => {

                    if (grandchild.children && grandchild.tipo === 'Tarea') {
                        return grandchild.children.flatMap(greatGrandchild => {

                            if (greatGrandchild.tipo === 'Indicador') {

                                const fecha = greatGrandchild.meta_e?.[0]?.fecha;
                                let fechaFormateada = '-'; // Valor por defecto

                                if (fecha && !isNaN(new Date(fecha).getTime())) {
                                    fechaFormateada = new Date(fecha).toLocaleDateString('es-ES');
                                }

                                // Cálculo del porcentaje de ejecución
                                const metaProyectada = greatGrandchild.meta_proyectada || 0;
                                const valorEjecutado = Math.floor((greatGrandchild.meta_e?.reduce((total, meta) => total + meta.valor, 0) || 0) * 100) / 100;
                                const porcentajeEjecucion = metaProyectada > 0 ? (valorEjecutado / metaProyectada) * 100 : 0;
                                const datoIndicador = greatGrandchild;
                                return {
                                    key: greatGrandchild.key,
                                    codigo_tarea: grandchild.codigo_tarea,
                                    tarea: grandchild.tarea,
                                    indicador: greatGrandchild.title,
                                    meta_proyectada: metaProyectada,
                                    valorEjecutado: valorEjecutado,
                                    porcentaje_ejecucion: porcentajeEjecucion.toFixed(2) + '%', // Mostrar el porcentaje con 2 decimales
                                    porcentaje_avance: greatGrandchild.meta_e?.[0]?.valor || 'N/A',
                                    fechas: fechaFormateada,
                                    responsables: 'N/A',
                                    soportes: 'N/A',
                                    datoIndicador: datoIndicador,
                                };
                            }
                            return [];
                        });
                    }
                    return [];
                });

                // Cálculo del porcentaje general de ejecución para la actividad
                const totalMetaProyectada = actividadData.reduce((total, data) => total + (data.meta_proyectada || 0), 0);
                const totalValorEjecutado = actividadData.reduce((total, data) => total + (data.meta_proyectada * (parseFloat(data.porcentaje_ejecucion) / 100) || 0), 0);
                const porcentajeEjecucionActividad = totalMetaProyectada > 0 ? (totalValorEjecutado / totalMetaProyectada) * 100 : 0;

                return [{
                    actividadTitle: child.title,
                    actividad: child.actividad, // Incluye el campo actividad
                    porcentajeEjecucionActividad: porcentajeEjecucionActividad.toFixed(2), // Solo el valor numérico para usar en Progress
                    actividadData: actividadData
                }];
            }
            return [];
        });
    };

    const actividades = extractData(nodo.children) || [];
    const columns = [
        {
            title: 'No.',
            dataIndex: 'codigo_tarea',
            key: 'codigo_tarea',
            width: 20,
            align: 'center',
            fixed: 'left',
        },
        {
            title: 'Tarea',
            dataIndex: 'tarea',
            key: 'tarea',
            width: 180,
            align: 'left',
            render: text => (
                <Tooltip title={text}>
                    <div className="truncated-text">{text.length > 25 ? `${text.slice(0, 25)}...` : text}</div>
                </Tooltip>
            ),
        },
        {
            title: 'Indicador',
            dataIndex: 'indicador',
            key: 'indicador',
            width: 180,
            align: 'left',
            render: text => (
                <Tooltip title={text}>
                    <div className="truncated-text">{text.length > 25 ? `${text.slice(0, 25)}...` : text}</div>
                </Tooltip>
            ),
        },
        {
            title: 'Meta',
            dataIndex: 'meta_proyectada',
            key: 'meta_proyectada',
            width: 15,
            align: 'center',
        }, {
            title: 'Avance',
            dataIndex: 'valorEjecutado',
            key: 'valorEjecutado',
            width: 15,
            align: 'center',
        },
        {
            title: '% Ejecución',
            dataIndex: 'porcentaje_ejecucion',
            key: 'porcentaje_ejecucion',
            width: 150,
            align: 'center',
            render: (percent) => (
                <Progress
                    percent={parseFloat(percent)} // Asegúrate de que el valor sea numérico
                    strokeColor={{
                        '0%': '#042956',
                        '50%': '#338bc7',
                        '100%': '#87d068',
                    }}
                    showInfo={true} // Oculta el texto del porcentaje en la barra
                />
            ),
        },

    ];

    const items = actividades.map((actividad, index) => ({
        key: `panel-${index}`,
        title: (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{actividad.actividadTitle} - {actividad.actividad}</span>
                <div style={{ marginLeft: '10px', width: '250px' }}>
                    <Progress
                        percent={parseFloat(actividad.porcentajeEjecucionActividad)}
                        strokeColor={{
                            '0%': '#338bc7',
                            '100%': '#87d068',
                        }}
                        showInfo={true}
                        size="small"
                    />
                </div>
            </div>
        ),
        children: (
            <Table
                dataSource={actividad.actividadData}
                columns={columns}
                pagination={false}
                scroll={{ x: 'max-content' }} // Habilita desplazamiento horizontal si el contenido es demasiado ancho
            />
        ),
    }));



    return (
        <div className="container-fluid py-3">
            <Collapse>
                {items.map(item => (
                    <Collapse.Panel
                        key={item.key}
                        header={item.title}
                        className="custom-panel"
                    >
                        {item.children}
                    </Collapse.Panel>
                ))}
            </Collapse>
            <PoaMediosVerificacion isVisible={isModalVisible} onClose={hideModal} record={selectedRecord} />
        </div>
    );
};

export default PoaActividades;
